import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ReactComponent as MinusIcon } from '../../../assets/images/svg/quantity-minus.svg';
import { ReactComponent as PlusIcon } from '../../../assets/images/svg/quantity-plus.svg';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import { mediaBelow } from '../../../assets/styles/helper';
import useSfhGtmHook from '../../../util/hooks/useSfhGtmHook';
import {
  SFH_QTY_MANUAL_INPUT,
  SFH_QTY_PLUS_CLICKED,
  SFH_QTY_MINUS_CLICKED,
} from '../../../constants/gtmEvents';

const StepperRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  padding: 24px 0;
`;

const StepperButton = styled.button`
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${themeColors.colorBackgroundSecondary};
  border: 1px solid ${themeColors.colorCardBorder};
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.15s ease;
  padding: 0;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: ${themeColors.colorBorderPrimary};
  }

  > svg {
    width: 20px;
    height: 20px;
  }
  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: 36px;
    height: 36px;
    > svg {
      width: 22px;
      height: 22px;
    }
  }
`;

const ValueInput = styled.input`
  font-family: ${variables.family.goldSilverHeaders}, sans-serif;
  font-size: 42px;
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
  width: 120px;
  text-align: center;
  line-height: 48px;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  outline: none;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 36px;
  }
`;

const BLOCKED_KEYS = ['.', ',', '-', '+', 'e', 'E'];
// Cap the typed quantity at 10 digits, matching the Buy wizard input.
const MAX_QUANTITY_CHARS = 10;
export const MAX_QUANTITY_VALUE = 10 ** MAX_QUANTITY_CHARS - 1;

const SellFromHomeQuantityStepper = ({
  value,
  onChange,
  min,
  incrementDisabled,
  sku,
  onIncrementClick,
  onDecrementClick,
}) => {
  const { sfhGtmEvent } = useSfhGtmHook();
  const [draft, setDraft] = useState(String(value));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) setDraft(String(value));
  }, [value, isFocused]);

  const clampInput = (n) => {
    if (n < min) return min;
    return Math.min(MAX_QUANTITY_VALUE, n);
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setDraft('');
      if (value !== min) onChange(min);
      return;
    }
    if (!/^\d+$/.test(raw)) return;
    if (raw.length > MAX_QUANTITY_CHARS) return;
    const next = clampInput(Number(raw));
    setDraft(String(next));
    if (next !== value) onChange(next);
  };

  const handleBlur = () => {
    setIsFocused(false);
    setDraft(String(value));
  };

  const decrement = () => {
    if (value > min) {
      sfhGtmEvent(SFH_QTY_MINUS_CLICKED, { sku });
      if (onDecrementClick) onDecrementClick();
      onChange(value - 1);
    }
  };

  const increment = () => {
    if (!incrementDisabled) {
      sfhGtmEvent(SFH_QTY_PLUS_CLICKED, { sku });
      if (onIncrementClick) onIncrementClick();
      onChange(value + 1);
    }
  };

  return (
    <StepperRow>
      <StepperButton
        type="button"
        onClick={decrement}
        disabled={value <= min}
        aria-label="Decrease quantity"
        data-cy="button-sfh-quantity-decrement"
      >
        <MinusIcon />
      </StepperButton>
      <ValueInput
        type="number"
        inputMode="numeric"
        value={draft}
        onChange={handleChange}
        onFocus={(e) => {
          sfhGtmEvent(SFH_QTY_MANUAL_INPUT, { sku });
          setIsFocused(true);
          e.target.select();
        }}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (BLOCKED_KEYS.includes(e.key)) e.preventDefault();
        }}
        aria-label="Quantity"
        data-cy="input-sfh-quantity"
      />
      <StepperButton
        type="button"
        onClick={increment}
        disabled={incrementDisabled}
        aria-label="Increase quantity"
        data-cy="button-sfh-quantity-increment"
      >
        <PlusIcon />
      </StepperButton>
    </StepperRow>
  );
};

SellFromHomeQuantityStepper.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  incrementDisabled: PropTypes.bool,
  sku: PropTypes.string,
  onIncrementClick: PropTypes.func,
  onDecrementClick: PropTypes.func,
};

SellFromHomeQuantityStepper.defaultProps = {
  min: 0,
  incrementDisabled: false,
  sku: '',
  onIncrementClick: undefined,
  onDecrementClick: undefined,
};

export default SellFromHomeQuantityStepper;
